import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../../connector/api";
import toast, { Toaster } from "react-hot-toast";
import { 
  ArrowLeft, 
  IdCard, 
  SquarePen, 
  Trash, 
  Camera, 
  Search, 
  Calendar, 
  History, 
  RotateCcw, 
  ChevronLeft, 
  ChevronRight, 
  BookOpen, 
  Clock,
  AlertTriangle,
  UserCheck
} from "lucide-react";

import AppLayout from "../components/common/AppLagout";
import NewPatronModal from "../components/NewPatronModal";

export default function ViewPatron() {
  const { id } = useParams();
  const navigate = useNavigate();

  // --- 1. STATE & HOOK DECLARATIONS ---
  const [patron, setPatron] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const [loanHistory, setLoanHistory] = useState([]);
  const [loadingLoans, setLoadingLoans] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Photo Upload State
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Header / Global Book Search State
  const [headerSearchTerm, setHeaderSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const searchRef = useRef(null);

  // Helper for normalizing cover image URLs
  const getCoverUrl = (path) => {
    if (!path) return null;
    if (path.startsWith("http://") || path.startsWith("https://")) return path;
    const base = api.defaults.baseURL
      ? api.defaults.baseURL.replace(/\/api\/?$/, "")
      : "";
    const normalizedPath = path.startsWith("/") ? path : `/${path}`;
    return `${base}${normalizedPath}`;
  };

  // --- 2. API CALLS & EFFECTS ---
  const fetchPatron = async () => {
    try {
      const res = await api.get(`/patrons/${id}/`);
      setPatron(res.data);
    } catch {
      toast.error("Failed to load patron profile");
    }
  };

  const fetchLoanHistory = async () => {
    try {
      setLoadingLoans(true);
      const res = await api.get(`/patrons/${id}/loans/`);
      setLoanHistory(res.data);
    } catch {
      toast.error("Failed to load loan history");
    } finally {
      setLoadingLoans(false);
    }
  };

  useEffect(() => {
    fetchPatron();
    fetchLoanHistory();
  }, [id]);

  // Debounced Live Header Search Effect
  useEffect(() => {
    if (!headerSearchTerm.trim()) {
      setSearchResults([]);
      setShowDropdown(false);
      return;
    }

    setSearchLoading(true);
    const handler = setTimeout(async () => {
      try {
        const res = await api.get("/cataloging/dashboard/search", {
          params: { q: headerSearchTerm },
        });
        const data = res.data || [];
        const normalizedResults = data.map((b) => ({
          ...b,
          cover_url: getCoverUrl(b.cover_image),
          is_available: b.status === "available",
        }));
        setSearchResults(normalizedResults);
        setShowDropdown(true);
      } catch (err) {
        console.error("Header search failed:", err);
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 400);

    return () => clearTimeout(handler);
  }, [headerSearchTerm]);

  // --- 3. HANDLERS ---
  const handleUpdatePatron = async (data) => {
    try {
      const formData = new FormData();
      Object.keys(data).forEach((key) => {
        if (key === "cover_image" && data.cover_image instanceof File) {
          formData.append("cover_image", data.cover_image);
        } else {
          formData.append(key, data[key]);
        }
      });
      const res = await api.put(`/patrons/${id}/`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPatron(res.data);
      toast.success("Patron updated successfully");
      setEditModalOpen(false);
    } catch {
      toast.error("Failed to update patron");
    }
  };

  const handleDeletePatron = async () => {
    try {
      await api.delete(`/patrons/${id}/`);
      toast.success("Patron deleted successfully");
      navigate("/library/patrons");
    } catch (error) {
      console.error("Delete Error:", error);
      toast.error("Failed to delete patron");
    }
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) setPreview(URL.createObjectURL(file));
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    const file = document.getElementById("photoInput")?.files[0];
    if (!file) return toast.error("Choose a photo first");

    const formData = new FormData();
    formData.append("image", file);

    try {
      setUploading(true);
      await api.post(`/patrons/${id}/upload-photo/`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Photo uploaded successfully");
      setPreview(null);
      fetchPatron();
    } catch {
      toast.error("Failed to upload photo");
    } finally {
      setUploading(false);
    }
  };

  // Updated route navigation for selecting a book from the header
  const handleSelectBook = (selectedBook) => {
    const bookId = selectedBook?.id || selectedBook?._id;
    if (!bookId) return;

    setShowDropdown(false);
    setHeaderSearchTerm("");
    navigate(`/library/cataloging/book/detail/${bookId}`);
  };

  // --- 4. COMPUTED DATA & PAGINATION ---
  const filteredLoans = loanHistory.filter((loan) => {
    const borrower = (loan.borrower || "").toLowerCase();
    const bookTitle = (loan.book_title || "").toLowerCase();
    const barcode = (loan.barcode || "").toLowerCase();
    const term = searchTerm.toLowerCase();

    const matchesSearch =
      borrower.includes(term) || bookTitle.includes(term) || barcode.includes(term);

    const borrowDate = new Date(loan.borrow_date);
    const matchesFromDate = !fromDate || borrowDate >= new Date(fromDate);
    const matchesToDate = !toDate || borrowDate <= new Date(toDate);

    return matchesSearch && matchesFromDate && matchesToDate;
  });

  const totalPages = Math.ceil(filteredLoans.length / itemsPerPage);
  const currentItems = filteredLoans.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const formatLocalDateString = (dateInput) => {
    if (!dateInput) return "—";
    try {
      const dateObj = new Date(dateInput);
      if (isNaN(dateObj.getTime())) return "—";
      return dateObj.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return "—";
    }
  };

  // Header Props configuration
  const headerProps = {
    title: patron ? `Patrons / ${patron.firstname} ${patron.surname}` : "Patron Profile",
    searchTerm: headerSearchTerm,
    setSearchTerm: setHeaderSearchTerm,
    searchRef,
    searchLoading,
    searchResults,
    showDropdown,
    setShowDropdown,
    highlightedIndex,
    setHighlightedIndex,
    handleSelectBook,
  };

  // --- 5. EARLY RETURN (LOADING STATE) ---
  if (!patron) {
    return (
      <AppLayout headerProps={headerProps}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-pulse flex flex-col items-center gap-3">
            <div className="h-6 w-6 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
            <p className="text-xs text-stone-400 font-medium">Loading patron records...</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  // --- 6. MAIN RENDER ---
  return (
    <AppLayout headerProps={headerProps}>
      <Toaster 
        position="top-right" 
        toastOptions={{
          style: {
            background: "#1a1613",
            color: "#f5f5f4",
            border: "1px solid #2e2720"
          }
        }} 
      />

      <div className="max-w-6xl mx-auto space-y-6 text-stone-200 font-dynamic-title pb-8">

        {/* BREADCRUMB & TOP NAV */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-4 border-b border-[#2e2720]">
          <div>
            <div className="flex items-center gap-2 text-[10px] font-sans font-semibold tracking-wider text-stone-500 uppercase">
              <span 
                className="hover:text-amber-400 cursor-pointer transition-colors" 
                onClick={() => navigate("/library/patrons")}
              >
                Patrons
              </span>
              <span>/</span>
              <span className="text-amber-400">Profile Details</span>
            </div>
            <h1 className="text-lg font-serif font-bold tracking-tight text-stone-100 mt-1">
              {patron.title && `${patron.title}. `}{patron.firstname} {patron.surname}
            </h1>
          </div>

          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center justify-center gap-2 px-4 py-2 text-xs font-bold text-stone-300 bg-[#131110] border border-[#2e2720] rounded-xl shadow-sm hover:bg-[#201c17] hover:text-stone-100 transition-all self-start sm:self-center"
          >
            <ArrowLeft className="h-4 w-4" /> Back to Patrons
          </button>
        </div>

        {/* PATRON PROFILE CARD */}
        <div className="bg-[#131110] rounded-2xl border border-[#2e2720] shadow-xl overflow-hidden p-6 md:p-8">
          <div className="flex flex-col md:flex-row gap-8 items-start">

            {/* Profile Avatar Container */}
            <div className="w-full md:w-44 h-56 shrink-0 bg-[#1a1613] rounded-xl border border-[#2e2720] overflow-hidden relative group shadow-inner">
              {preview ? (
                <img src={preview} alt="Preview" className="w-full h-full object-cover" />
              ) : patron.photo ? (
                <img src={patron.photo.image} alt="Patron Profile" className="w-full h-full object-cover" />
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-stone-500 gap-2">
                  <IdCard className="h-10 w-10 stroke-1 text-stone-600" />
                  <span className="text-xs font-medium">No Photo Available</span>
                </div>
              )}

              <label className="absolute inset-0 bg-black/60 backdrop-blur-xs flex flex-col items-center justify-center gap-1 text-amber-400 text-xs font-bold cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                <Camera className="h-5 w-5" />
                <span>{uploading ? "Uploading..." : "Update Image"}</span>
                <input
                  id="photoInput"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={async (e) => {
                    handlePhotoChange(e);
                    await handleUpload(e);
                  }}
                />
              </label>
            </div>

            {/* Core Details Grid */}
            <div className="flex-1 space-y-6 w-full">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 bg-amber-500/10 border border-amber-500/20 text-amber-400 text-[11px] font-bold rounded-md uppercase tracking-wider mb-1">
                    <UserCheck className="w-3.5 h-3.5" />
                    Verified Patron
                  </span>
                  <h2 className="text-lg font-bold text-stone-100">Account Details</h2>
                </div>

                {/* Profile Controls */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setEditModalOpen(true)}
                    className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#1a1613] border border-[#2e2720] text-stone-300 rounded-xl text-xs font-bold hover:bg-[#201c17] hover:text-stone-100 transition"
                  >
                    <SquarePen className="h-3.5 w-3.5 text-amber-400" /> Edit Info
                  </button>
                  <button
                    onClick={() => setDeleteModalOpen(true)}
                    className="inline-flex items-center gap-2 px-3 py-1.5 bg-red-500/10 border border-red-500/20 text-red-400 rounded-xl text-xs font-bold hover:bg-red-500/20 transition"
                  >
                    <Trash className="h-3.5 w-3.5 text-red-400" /> Remove
                  </button>
                </div>
              </div>

              <hr className="border-[#2e2720]" />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
                <div className="bg-[#1a1613] p-4 rounded-xl flex items-center gap-3 border border-[#2e2720]">
                  <div className="p-2 bg-[#201c17] rounded-lg border border-[#3e352c] text-amber-400">
                    <IdCard className="h-4 w-4" />
                  </div>
                  <div>
                    <p className="text-[10px] text-stone-500 font-bold uppercase tracking-wider">Card Number</p>
                    <p className="font-mono text-stone-200 font-bold text-sm">{patron.cardnumber || "N/A"}</p>
                  </div>
                </div>

                <div className="bg-[#1a1613] p-4 rounded-xl flex items-center gap-3 border border-[#2e2720]">
                  <div className="p-2 bg-[#201c17] rounded-lg border border-[#3e352c] text-amber-400">
                    <Calendar className="h-4 w-4" />
                  </div>
                  <div>
                    <p className="text-[10px] text-stone-500 font-bold uppercase tracking-wider">Registration Date</p>
                    <p className="font-semibold text-stone-200 text-sm">
                      {patron.date_registered ? new Date(patron.date_registered).toLocaleDateString(undefined, { dateStyle: "medium" }) : "N/A"}
                    </p>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>

        {/* CIRCULATION LEDGER / HISTORY CARD */}
        <div className="bg-[#131110] rounded-2xl border border-[#2e2720] shadow-xl overflow-hidden">
          
          {/* Section Header */}
          <div className="p-6 border-b border-[#2e2720] bg-[#161311] flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-amber-500/10 border border-amber-500/20 text-amber-400 rounded-xl">
                <History className="h-5 w-5" />
              </div>
              <div>
                <h2 className="text-base font-bold text-stone-100">Loan Activity History</h2>
                <p className="text-xs text-stone-400 mt-0.5">Audit resource checkouts, active terms, and return logs.</p>
              </div>
            </div>
          </div>

          {/* Filters Bar */}
          <div className="p-4 bg-[#1a1613]/50 border-b border-[#2e2720] flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-3 text-xs">
            
            {/* Search Box */}
            <div className="w-full lg:max-w-md relative">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-stone-500" />
              <input
                type="text"
                placeholder="Search by book title or barcode..."
                className="w-full bg-[#131110] border border-[#2e2720] pl-9 pr-3 py-2 rounded-xl text-stone-200 placeholder-stone-500 focus:outline-none focus:border-amber-500/50 transition"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>

            {/* Date Filters & Reset */}
            <div className="flex flex-wrap items-center gap-2.5 text-stone-400">
              <div className="flex items-center gap-2 bg-[#131110] border border-[#2e2720] px-3 py-1.5 rounded-xl">
                <span className="text-[10px] font-bold uppercase tracking-wider text-stone-500">From</span>
                <input
                  type="date"
                  className="bg-transparent border-none p-0 text-xs font-medium focus:outline-none text-stone-200 font-mono"
                  value={fromDate}
                  onChange={(e) => {
                    setFromDate(e.target.value);
                    setCurrentPage(1);
                  }}
                />
              </div>

              <div className="flex items-center gap-2 bg-[#131110] border border-[#2e2720] px-3 py-1.5 rounded-xl">
                <span className="text-[10px] font-bold uppercase tracking-wider text-stone-500">To</span>
                <input
                  type="date"
                  className="bg-transparent border-none p-0 text-xs font-medium focus:outline-none text-stone-200 font-mono"
                  value={toDate}
                  onChange={(e) => {
                    setToDate(e.target.value);
                    setCurrentPage(1);
                  }}
                />
              </div>

              {(searchTerm || fromDate || toDate) && (
                <button
                  type="button"
                  onClick={() => {
                    setSearchTerm("");
                    setFromDate("");
                    setToDate("");
                    setCurrentPage(1);
                  }}
                  className="inline-flex items-center gap-1.5 px-3 py-2 border border-[#2e2720] rounded-xl font-bold bg-[#131110] hover:bg-[#201c17] text-amber-400 transition"
                >
                  <RotateCcw className="h-3.5 w-3.5" />
                  <span>Reset</span>
                </button>
              )}
            </div>
          </div>

          {/* Table Container */}
          <div className="min-h-[220px] flex flex-col justify-between">
            {loadingLoans ? (
              <div className="p-16 text-center text-stone-500 flex flex-col items-center justify-center gap-2">
                <div className="h-5 w-5 border-2 border-stone-600 border-t-amber-400 rounded-full animate-spin" />
                <span className="text-xs font-medium mt-1">Retrieving loan history records...</span>
              </div>
            ) : currentItems.length === 0 ? (
              <div className="p-16 text-center text-stone-500 flex flex-col items-center justify-center gap-3">
                <BookOpen className="h-8 w-8 text-stone-600" />
                <div>
                  <p className="text-sm font-bold text-stone-300">No Historical Records Found</p>
                  <p className="text-xs text-stone-500 max-w-xs mx-auto mt-0.5">No database logs match your current search or date filters.</p>
                </div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse text-xs">
                  <thead>
                    <tr className="bg-[#1a1613] border-b border-[#2e2720] text-[11px] font-bold uppercase tracking-wider text-stone-400 select-none">
                      <th className="px-6 py-3.5">Book Title / Asset</th>
                      <th className="px-6 py-3.5">Checkout Date</th>
                      <th className="px-6 py-3.5">Expected Due</th>
                      <th className="px-6 py-3.5">Check-In Date</th>
                      <th className="px-6 py-3.5 text-right">Status</th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-[#2e2720] text-stone-300">
                    {currentItems.map((loan) => {
                      const todayStr = new Date().toISOString().split("T")[0];
                      const dueStr = loan.due_date ? new Date(loan.due_date).toISOString().split("T")[0] : "";
                      const hasReturned = !!loan.returned_date;

                      let badgeStyles = "bg-blue-500/10 text-blue-400 border-blue-500/20";
                      let dotStyles = "bg-blue-400";
                      let statusLabel = "Active Loan";

                      if (!hasReturned && dueStr < todayStr) {
                        statusLabel = "Overdue Record";
                        badgeStyles = "bg-red-500/10 text-red-400 border-red-500/20";
                        dotStyles = "bg-red-500 animate-pulse";
                      } else if (!hasReturned && dueStr === todayStr) {
                        statusLabel = "Due Today";
                        badgeStyles = "bg-amber-500/10 text-amber-400 border-amber-500/20";
                        dotStyles = "bg-amber-400";
                      } else if (hasReturned) {
                        statusLabel = "Returned";
                        badgeStyles = "bg-emerald-500/10 text-emerald-400 border-emerald-500/20";
                        dotStyles = "bg-emerald-400";
                      }

                      return (
                        <tr key={loan.id} className="hover:bg-[#1a1613]/60 transition-colors group">
                          {/* Title */}
                          <td className="px-6 py-4 max-w-md">
                            <div className="flex items-center gap-3 min-w-0">
                              <div className="p-1.5 bg-[#1a1613] border border-[#2e2720] text-stone-400 rounded-lg shrink-0">
                                <BookOpen className="h-4 w-4 text-amber-400" />
                              </div>
                              <div className="min-w-0">
                                <p className="font-semibold text-stone-200 truncate group-hover:text-amber-300 transition-colors">
                                  {loan.book_title || "Untitled Catalog Asset"}
                                </p>
                                {loan.barcode && (
                                  <p className="text-[10px] font-mono text-stone-500 mt-0.5">Tag ID: {loan.barcode}</p>
                                )}
                              </div>
                            </div>
                          </td>

                          {/* Checkout */}
                          <td className="px-6 py-4 text-stone-400 font-medium whitespace-nowrap">
                            {formatLocalDateString(loan.borrow_date)}
                          </td>

                          {/* Due Date */}
                          <td className="px-6 py-4 text-stone-400 font-medium whitespace-nowrap">
                            {formatLocalDateString(loan.due_date)}
                          </td>

                          {/* Returned Date */}
                          <td className="px-6 py-4 whitespace-nowrap">
                            {loan.returned_date ? (
                              <span className="text-stone-300 font-medium">{formatLocalDateString(loan.returned_date)}</span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-[11px] text-stone-500 font-medium italic bg-[#1a1613] px-2 py-0.5 border border-[#2e2720] rounded-md">
                                <Clock className="h-3 w-3 text-amber-500" />
                                <span>Outstanding</span>
                              </span>
                            )}
                          </td>

                          {/* Status */}
                          <td className="px-6 py-4 text-right whitespace-nowrap">
                            <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 text-xs font-bold rounded-lg border ${badgeStyles}`}>
                              <span className={`h-1.5 w-1.5 rounded-full ${dotStyles}`} />
                              {statusLabel}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                {/* Pagination Controls */}
                <div className="flex items-center justify-between px-6 py-3.5 bg-[#1a1613]/40 border-t border-[#2e2720] select-none text-xs">
                  <button
                    type="button"
                    disabled={currentPage <= 1}
                    onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                    className="inline-flex items-center gap-1 px-3 py-1.5 border border-[#2e2720] bg-[#131110] rounded-xl font-bold text-stone-300 hover:bg-[#201c17] hover:text-stone-100 disabled:opacity-40 disabled:cursor-not-allowed transition"
                  >
                    <ChevronLeft className="h-3.5 w-3.5" />
                    <span>Previous</span>
                  </button>

                  <span className="text-stone-500 font-medium">
                    Page <strong className="text-stone-200">{currentPage}</strong> of <strong className="text-stone-200">{totalPages || 1}</strong>
                  </span>

                  <button
                    type="button"
                    disabled={currentPage >= totalPages}
                    onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                    className="inline-flex items-center gap-1 px-3 py-1.5 border border-[#2e2720] bg-[#131110] rounded-xl font-bold text-stone-300 hover:bg-[#201c17] hover:text-stone-100 disabled:opacity-40 disabled:cursor-not-allowed transition"
                  >
                    <span>Next</span>
                    <ChevronRight className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            )}
          </div>

        </div>

      </div>

      {/* EDIT PATRON MODAL */}
      {editModalOpen && (
        <NewPatronModal
          initialData={patron}
          onClose={() => setEditModalOpen(false)}
          onSave={handleUpdatePatron}
        />
      )}

      {/* DELETE CONFIRMATION MODAL */}
      {deleteModalOpen && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn font-dynamic-title">
          <div className="bg-[#1a1613] rounded-2xl shadow-2xl max-w-md w-full border border-[#2e2720] overflow-hidden transform transition-all">
            
            <div className="p-6 flex flex-col sm:flex-row items-start gap-4">
              <div className="p-3 bg-red-500/10 border border-red-500/20 text-red-400 rounded-2xl shrink-0 mx-auto sm:mx-0">
                <AlertTriangle className="h-6 w-6" />
              </div>

              <div className="flex-1 space-y-1.5 text-center sm:text-left w-full">
                <h3 className="text-base font-bold text-stone-100">Delete Patron Account</h3>
                <p className="text-xs text-stone-400 leading-relaxed">
                  Are you sure you want to permanently remove <span className="font-bold text-stone-200 bg-[#131110] px-1.5 py-0.5 rounded border border-[#2e2720]">{patron.firstname} {patron.surname}</span>?
                </p>
                <p className="text-xs text-red-400 bg-red-500/10 border border-red-500/20 p-3 rounded-xl mt-3 font-semibold">
                  Warning: All borrowing history, active tags, and record profile links will be deleted.
                </p>
              </div>
            </div>

            <div className="px-6 py-4 bg-[#131110] flex flex-col-reverse sm:flex-row justify-end gap-2.5 border-t border-[#2e2720] text-xs font-bold">
              <button
                type="button"
                onClick={() => setDeleteModalOpen(false)}
                className="w-full sm:w-auto px-4 py-2 border border-[#2e2720] bg-[#1a1613] text-stone-300 rounded-xl hover:bg-[#201c17] hover:text-stone-100 transition"
              >
                Cancel, Keep Profile
              </button>
              <button
                type="button"
                onClick={handleDeletePatron}
                className="w-full sm:w-auto px-5 py-2 bg-red-600 hover:bg-red-500 text-white rounded-xl shadow transition"
              >
                Confirm Delete
              </button>
            </div>

          </div>
        </div>
      )}
    </AppLayout>
  );
}